/**
 * MobileAppManagement.jsx
 * Admin component for managing mobile app settings, push notifications, and store deployment
 * Refactored: Screen components moved to ./mobile-preview/screens/
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Smartphone, Bell, Upload, Download, Settings, CheckCircle, 
  XCircle, Clock, Send, RefreshCw, Apple, PlayCircle, 
  FileText, AlertTriangle, ChevronDown, ChevronUp, Eye, Save,
  Snowflake, Image, Palette, PartyPopper, Flower2,
  Zap, Users, Sparkles as SparklesIcon, Camera, BarChart3, Heart, Trophy, Watch,
  Megaphone, Wand2
} from 'lucide-react';

// Import modular AppPreview component
import { AppPreview } from './mobile-preview';
// Mobile-App-spezifische Sub-Panels
import PushStatusOverview from './PushStatusOverview';
import AppVersionConfig from './AppVersionConfig';
import AdminPushStatusCard from './AdminPushStatusCard';
import StartupBannerTab from './StartupBannerTab';
import MobilePromoTab from './MobilePromoTab';
import AppStoreStudio from './AppStoreStudio';
import { Switch } from '@/components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MobileAppManagement = ({ token }) => {
  const [activeTab, setActiveTab] = useState('preview');
  const [storeInfo, setStoreInfo] = useState(null);
  const [buildStatus, setBuildStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Mobile App Settings (Branding & Effects)
  const [appSettings, setAppSettings] = useState({
    branding: {
      logo_url: '',  // Statisches Logo (hell)
      logo_dark_url: '',  // Statisches Logo (dunkel/Mobile App)
      splash_video_url: '',  // Animiertes Video für Splash Screen
      logo_size: 100, // Logo size in percentage (50-150)
      logo_type: 'image', // 'image', 'gif', 'video'
      splash_logo_size: 280, // Splash Screen Logo Grösse in px
      splash_frame_size: 110, // Splash Screen Rahmen Grösse in px
      primary_color: '#C9A227',
      accent_color: '#D4B645',
      app_name: ''
    },
    seasonal_effects: {
      // Snow
      snow_enabled: false,
      snow_intensity: 'medium',
      snow_speed: 'medium',
      snow_auto_enabled: false,
      snow_start_date: '12-01',
      snow_end_date: '02-28',
      winter_hat_enabled: false,
      // Confetti
      confetti_enabled: false,
      confetti_intensity: 'medium',
      confetti_auto_enabled: false,
      confetti_start_date: '12-31',
      confetti_end_date: '01-02',
      // Cherry Blossom
      cherry_blossom_enabled: false,
      cherry_blossom_intensity: 'medium',
      cherry_blossom_auto_enabled: false,
      cherry_blossom_start_date: '03-20',
      cherry_blossom_end_date: '04-30',
      // Fireworks
      fireworks_enabled: false,
      fireworks_intensity: 'medium',
      fireworks_auto_enabled: false,
      fireworks_start_date: '12-31',
      fireworks_end_date: '01-01'
    }
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [storeRes, buildRes, settingsRes] = await Promise.allSettled([
        axios.get(`${API}/admin/mobile-app/store-info`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/mobile-app/build-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/mobile-app/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (storeRes.status === 'fulfilled') {
        setStoreInfo(storeRes.value.data);
      }
      if (buildRes.status === 'fulfilled') {
        setBuildStatus(buildRes.value.data);
      }
      if (settingsRes.status === 'fulfilled' && settingsRes.value.data) {
        setAppSettings(prev => ({
          branding: { ...prev.branding, ...settingsRes.value.data.branding },
          seasonal_effects: { ...prev.seasonal_effects, ...settingsRes.value.data.seasonal_effects }
        }));
      }

      if ([storeRes, buildRes].some(result => result.status === 'rejected')) {
        console.error('Error loading mobile app data:', {
          store: storeRes.status === 'rejected' ? storeRes.reason : null,
          build: buildRes.status === 'rejected' ? buildRes.reason : null
        });
        toast.error('Fehler beim Laden der Daten');
      }

      if (settingsRes.status === 'rejected') {
        console.error('Error loading app settings:', settingsRes.reason);
      }
    } catch (error) {
      console.error('Error loading mobile app data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  // Save app settings
  const saveAppSettings = async () => {
    setSettingsSaving(true);
    try {
      await axios.put(
        `${API}/admin/mobile-app/settings`,
        appSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSettingsSaving(false);
    }
  };

  // Logo upload handler
  const handleLogoUpload = async (event, isDarkMode = false) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setLogoUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/admin/mobile-app/upload-logo`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      if (response.data.success) {
        const logoUrl = response.data.url;
        const logoType = ['mp4', 'mov', 'webm'].includes(response.data.file_type) ? 'video' : 
                         response.data.file_type === 'gif' ? 'gif' : 'image';
        
        setAppSettings(prev => ({
          ...prev,
          branding: {
            ...prev.branding,
            [isDarkMode ? 'logo_dark_url' : 'logo_url']: logoUrl,
            logo_type: logoType
          }
        }));
        
        toast.success(`Logo hochgeladen (${logoType})`);
      }
    } catch (error) {
      toast.error('Fehler beim Hochladen: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLogoUploading(false);
    }
  };

  // Quick toggle for snow effect
  const toggleSnowEffect = async (enabled) => {
    const newSettings = {
      ...appSettings,
      seasonal_effects: { ...appSettings.seasonal_effects, snow_enabled: enabled }
    };
    setAppSettings(newSettings);
    
    try {
      await axios.put(
        `${API}/admin/mobile-app/settings/effects`,
        newSettings.seasonal_effects,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(enabled ? '❄️ Schneefall aktiviert' : 'Schneefall deaktiviert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Smartphone className="w-7 h-7 text-yellow-500" />
            Mobile App Verwaltung
          </h1>
          <p className="text-gray-500 mt-1">
            iOS & Android App-Einstellungen, Branding und Store-Deployment
          </p>
        </div>
        <button
          onClick={loadData}
          className="px-4 py-2 bg-gray-100 rounded-lg text-gray-900 hover:bg-gray-200 flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Aktualisieren
        </button>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-3">
        {[
          { id: 'preview', label: 'App Vorschau', icon: Eye },
          { id: 'branding', label: 'Branding & Effekte', icon: Palette },
          { id: 'features', label: 'Features & Toggles', icon: Zap },
          { id: 'startup-banner', label: 'Startup-Banner', icon: Megaphone },
          { id: 'mobile-promo', label: 'Promote to Mobile', icon: Smartphone },
          { id: 'app-store-studio', label: 'App Store Studio', icon: Wand2 },
          { id: 'push-status', label: 'App-Installationen', icon: Users },
          { id: 'overview', label: 'Store-Info', icon: Smartphone },
          { id: 'deployment', label: 'Store Deployment', icon: Upload },
          { id: 'download', label: 'Projekt Download', icon: Download },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`mobile-app-tab-${tab.id}`}
            className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm transition-colors ${
              activeTab === tab.id
                ? 'bg-yellow-500 text-black font-medium'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* App Preview Tab */}
      {activeTab === 'preview' && <AppPreview token={token} appSettings={appSettings} />}

      {/* Branding & Effects Tab */}
      {activeTab === 'branding' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Logo & Branding Card */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-yellow-500/20 rounded-xl">
                <Image className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Logo & Branding</h3>
                <p className="text-sm text-gray-500">App-Logo und Farben anpassen</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* App Name */}
              <div>
                <label className="block text-sm text-gray-500 mb-1">App Name</label>
                <input
                  type="text"
                  value={appSettings.branding.app_name}
                  onChange={(e) => setAppSettings(prev => ({
                    ...prev,
                    branding: { ...prev.branding, app_name: e.target.value }
                  }))}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:border-yellow-500 focus:outline-none"
                  placeholder="Fit Coach"
                />
              </div>
              
              {/* Splash Screen Video (Animiert) */}
              <div className="p-4 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 rounded-xl border border-yellow-500/30">
                <label className="block text-sm text-yellow-600 font-medium mb-2">🎬 Splash Screen Video (Animiert)</label>
                <p className="text-xs text-gray-500 mb-2">Wird beim App-Start als Animation angezeigt</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={appSettings.branding.splash_video_url || ''}
                    onChange={(e) => setAppSettings(prev => ({
                      ...prev,
                      branding: { ...prev.branding, splash_video_url: e.target.value }
                    }))}
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:border-yellow-500 focus:outline-none text-sm"
                    placeholder="Video-URL (.mov, .mp4)..."
                  />
                  <label className="px-3 py-2 bg-yellow-500 hover:bg-yellow-600 rounded-lg cursor-pointer flex items-center gap-1 text-black text-sm font-medium">
                    <Upload className="w-4 h-4" />
                    {logoUploading ? '...' : 'Upload'}
                    <input
                      type="file"
                      accept="video/*,.mov,.mp4,.webm"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setLogoUploading(true);
                        const formData = new FormData();
                        formData.append('file', file);
                        axios.post(`${API}/admin/mobile-app/upload-logo`, formData, {
                          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                        }).then(res => {
                          if (res.data.success) {
                            setAppSettings(prev => ({
                              ...prev,
                              branding: { ...prev.branding, splash_video_url: res.data.url }
                            }));
                            toast.success('Splash-Video hochgeladen');
                          }
                        }).catch(err => toast.error('Upload fehlgeschlagen'))
                          .finally(() => setLogoUploading(false));
                      }}
                      className="hidden"
                      disabled={logoUploading}
                    />
                  </label>
                </div>
                {appSettings.branding.splash_video_url && (
                  <div className="mt-2 p-2 bg-black rounded-lg inline-block">
                    <video 
                      src={appSettings.branding.splash_video_url} 
                      autoPlay 
                      loop 
                      muted 
                      playsInline
                      className="h-16 object-contain rounded"
                    />
                  </div>
                )}
              </div>

              {/* Statisches Logo (für Login & App) */}
              <div className="p-4 bg-gray-100 rounded-xl border border-gray-200">
                <label className="block text-sm text-gray-600 font-medium mb-2">🖼️ Statisches Logo (Login & App)</label>
                <p className="text-xs text-gray-500 mb-2">Wird nach dem Splash Screen angezeigt</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={appSettings.branding.logo_dark_url || ''}
                    onChange={(e) => setAppSettings(prev => ({
                      ...prev,
                      branding: { ...prev.branding, logo_dark_url: e.target.value }
                    }))}
                    className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 focus:border-yellow-500 focus:outline-none text-sm"
                    placeholder="Bild-URL (.png, .jpg)..."
                  />
                  <label className="px-3 py-2 bg-gray-300 hover:bg-gray-300 rounded-lg cursor-pointer flex items-center gap-1 text-gray-900 text-sm font-medium">
                    <Upload className="w-4 h-4" />
                    {logoUploading ? '...' : 'Upload'}
                    <input
                      type="file"
                      accept="image/*,.png,.jpg,.jpeg,.gif"
                      onChange={(e) => handleLogoUpload(e, true)}
                      className="hidden"
                      disabled={logoUploading}
                    />
                  </label>
                </div>
                {appSettings.branding.logo_dark_url && (
                  <div className="mt-2 p-3 bg-gray-50 rounded-lg inline-block">
                    <img 
                      src={appSettings.branding.logo_dark_url} 
                      alt="Logo Preview" 
                      className="h-12 object-contain"
                      style={{ height: `${(appSettings.branding.logo_size || 100) * 0.48}px` }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>
              
              {/* Logo Grösse */}
              <div>
                <label className="block text-sm text-gray-500 mb-1">
                  Logo-Grösse: {appSettings.branding.logo_size || 100}%
                </label>
                <input
                  type="range"
                  min="50"
                  max="150"
                  value={appSettings.branding.logo_size || 100}
                  onChange={(e) => setAppSettings(prev => ({
                    ...prev,
                    branding: { ...prev.branding, logo_size: parseInt(e.target.value) }
                  }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-yellow-500"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>50% (Klein)</span>
                  <span>100% (Normal)</span>
                  <span>150% (Gross)</span>
                </div>
              </div>
              
              {/* Splash Screen Grössen */}
              <div className="p-4 bg-yellow-500/10 rounded-xl border border-yellow-500/30 space-y-4">
                <h4 className="text-sm font-medium text-yellow-600">🎬 Splash Screen Grössen</h4>
                
                {/* Splash Logo Grösse */}
                <div>
                  <label className="block text-sm text-gray-500 mb-1">
                    Logo-Grösse: {appSettings.branding.splash_logo_size || 280}px
                  </label>
                  <input
                    type="range"
                    min="150"
                    max="400"
                    step="10"
                    value={appSettings.branding.splash_logo_size || 280}
                    onChange={(e) => setAppSettings(prev => ({
                      ...prev,
                      branding: { ...prev.branding, splash_logo_size: parseInt(e.target.value) }
                    }))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-yellow-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>150px</span>
                    <span>280px</span>
                    <span>400px</span>
                  </div>
                </div>
                
                {/* Splash Rahmen Grösse */}
                <div>
                  <label className="block text-sm text-gray-500 mb-1">
                    Rahmen-Grösse: {appSettings.branding.splash_frame_size || 110}px
                  </label>
                  <input
                    type="range"
                    min="60"
                    max="200"
                    step="10"
                    value={appSettings.branding.splash_frame_size || 110}
                    onChange={(e) => setAppSettings(prev => ({
                      ...prev,
                      branding: { ...prev.branding, splash_frame_size: parseInt(e.target.value) }
                    }))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-yellow-500"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>60px</span>
                    <span>130px</span>
                    <span>200px</span>
                  </div>
                </div>
              </div>
              
              {/* Primary Color */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Primärfarbe</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={appSettings.branding.primary_color}
                      onChange={(e) => setAppSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, primary_color: e.target.value }
                      }))}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={appSettings.branding.primary_color}
                      onChange={(e) => setAppSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, primary_color: e.target.value }
                      }))}
                      className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 text-sm font-mono"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-500 mb-1">Akzentfarbe</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={appSettings.branding.accent_color}
                      onChange={(e) => setAppSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, accent_color: e.target.value }
                      }))}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={appSettings.branding.accent_color}
                      onChange={(e) => setAppSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, accent_color: e.target.value }
                      }))}
                      className="flex-1 px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 text-sm font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Seasonal Effects Card */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <Snowflake className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Saisonale Effekte</h3>
                <p className="text-sm text-gray-500">Schneefall und andere Animationen</p>
              </div>
            </div>
            
            <div className="space-y-4">
              {/* Manual Snow Effect Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3">
                  <Snowflake className="w-5 h-5 text-blue-600" />
                  <div>
                    <p className="text-gray-900 font-medium">❄️ Schneefall manuell</p>
                    <p className="text-sm text-gray-500">Sofort aktivieren (überschreibt Zeitplan)</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleSnowEffect(!appSettings.seasonal_effects.snow_enabled)}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    appSettings.seasonal_effects.snow_enabled 
                      ? 'bg-blue-500' 
                      : 'bg-gray-300'
                  }`}
                >
                  <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    appSettings.seasonal_effects.snow_enabled 
                      ? 'translate-x-8' 
                      : 'translate-x-1'
                  }`} />
                </button>
              </div>
              
              {/* Auto Snow with Date Range */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-gray-900 font-medium">🗓️ Automatischer Zeitplan</p>
                      <p className="text-sm text-gray-500">Schnee im festgelegten Zeitraum</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setAppSettings(prev => ({
                      ...prev,
                      seasonal_effects: { 
                        ...prev.seasonal_effects, 
                        snow_auto_enabled: !prev.seasonal_effects.snow_auto_enabled 
                      }
                    }))}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      appSettings.seasonal_effects.snow_auto_enabled 
                        ? 'bg-blue-500' 
                        : 'bg-gray-300'
                    }`}
                  >
                    <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      appSettings.seasonal_effects.snow_auto_enabled 
                        ? 'translate-x-8' 
                        : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                {/* Date Range Inputs */}
                {appSettings.seasonal_effects.snow_auto_enabled && (
                  <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200">
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Von (Tag.Monat)</label>
                      <div className="flex gap-2">
                        <select
                          value={appSettings.seasonal_effects.snow_start_date?.split('-')[1] || '01'}
                          onChange={(e) => {
                            const month = appSettings.seasonal_effects.snow_start_date?.split('-')[0] || '12';
                            setAppSettings(prev => ({
                              ...prev,
                              seasonal_effects: { 
                                ...prev.seasonal_effects, 
                                snow_start_date: `${month}-${e.target.value}`
                              }
                            }));
                          }}
                          className="flex-1 px-2 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm"
                        >
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                            <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                          ))}
                        </select>
                        <select
                          value={appSettings.seasonal_effects.snow_start_date?.split('-')[0] || '12'}
                          onChange={(e) => {
                            const day = appSettings.seasonal_effects.snow_start_date?.split('-')[1] || '01';
                            setAppSettings(prev => ({
                              ...prev,
                              seasonal_effects: { 
                                ...prev.seasonal_effects, 
                                snow_start_date: `${e.target.value}-${day}`
                              }
                            }));
                          }}
                          className="flex-1 px-2 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm"
                        >
                          {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(m => (
                            <option key={m} value={m}>
                              {['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'][parseInt(m) - 1]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">Bis (Tag.Monat)</label>
                      <div className="flex gap-2">
                        <select
                          value={appSettings.seasonal_effects.snow_end_date?.split('-')[1] || '28'}
                          onChange={(e) => {
                            const month = appSettings.seasonal_effects.snow_end_date?.split('-')[0] || '02';
                            setAppSettings(prev => ({
                              ...prev,
                              seasonal_effects: { 
                                ...prev.seasonal_effects, 
                                snow_end_date: `${month}-${e.target.value}`
                              }
                            }));
                          }}
                          className="flex-1 px-2 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm"
                        >
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                            <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                          ))}
                        </select>
                        <select
                          value={appSettings.seasonal_effects.snow_end_date?.split('-')[0] || '02'}
                          onChange={(e) => {
                            const day = appSettings.seasonal_effects.snow_end_date?.split('-')[1] || '28';
                            setAppSettings(prev => ({
                              ...prev,
                              seasonal_effects: { 
                                ...prev.seasonal_effects, 
                                snow_end_date: `${e.target.value}-${day}`
                              }
                            }));
                          }}
                          className="flex-1 px-2 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-900 text-sm"
                        >
                          {['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'].map(m => (
                            <option key={m} value={m}>
                              {['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'][parseInt(m) - 1]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-span-2 text-xs text-gray-500">
                      Zeitraum: {appSettings.seasonal_effects.snow_start_date?.split('-')[1]}.{appSettings.seasonal_effects.snow_start_date?.split('-')[0]} 
                      {' → '}
                      {appSettings.seasonal_effects.snow_end_date?.split('-')[1]}.{appSettings.seasonal_effects.snow_end_date?.split('-')[0]}
                      {' '}(inkl. Jahreswechsel)
                    </div>
                  </div>
                )}
              </div>
              
              {/* Snow Intensity & Speed */}
              {(appSettings.seasonal_effects.snow_enabled || appSettings.seasonal_effects.snow_auto_enabled) && (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
                  <div>
                    <label className="block text-sm text-gray-500 mb-2">Schnee-Menge</label>
                    <div className="flex gap-2">
                      {[
                        { id: 'light', label: 'Wenig' },
                        { id: 'medium', label: 'Normal' },
                        { id: 'heavy', label: 'Viel' }
                      ].map(option => (
                        <button
                          key={option.id}
                          onClick={() => setAppSettings(prev => ({
                            ...prev,
                            seasonal_effects: { ...prev.seasonal_effects, snow_intensity: option.id }
                          }))}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                            appSettings.seasonal_effects.snow_intensity === option.id
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-2">Geschwindigkeit</label>
                    <div className="flex gap-2">
                      {[
                        { id: 'slow', label: '🐢 Langsam' },
                        { id: 'medium', label: '🚶 Normal' },
                        { id: 'fast', label: '🏃 Schnell' }
                      ].map(option => (
                        <button
                          key={option.id}
                          onClick={() => setAppSettings(prev => ({
                            ...prev,
                            seasonal_effects: { ...prev.seasonal_effects, snow_speed: option.id }
                          }))}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm transition-colors ${
                            appSettings.seasonal_effects.snow_speed === option.id
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Winter Hat Toggle */}
              {(appSettings.seasonal_effects.snow_enabled || appSettings.seasonal_effects.snow_auto_enabled) && (
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🎅</span>
                    <div>
                      <p className="text-gray-900 font-medium">Zipfelmütze auf Logo</p>
                      <p className="text-sm text-gray-500">Weihnachtliche Deko für das App-Logo</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setAppSettings(prev => ({
                      ...prev,
                      seasonal_effects: { 
                        ...prev.seasonal_effects, 
                        winter_hat_enabled: !prev.seasonal_effects.winter_hat_enabled 
                      }
                    }))}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      appSettings.seasonal_effects.winter_hat_enabled 
                        ? 'bg-red-500' 
                        : 'bg-gray-300'
                    }`}
                  >
                    <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      appSettings.seasonal_effects.winter_hat_enabled 
                        ? 'translate-x-8' 
                        : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              )}
              
              {/* Divider */}
              <div className="border-t border-gray-200 my-4" />
              
              {/* Confetti Effect (New Year) */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <PartyPopper className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="text-gray-900 font-medium">🎊 Konfetti (Neujahr)</p>
                      <p className="text-sm text-gray-500">Feierliche Atmosphäre</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setAppSettings(prev => ({
                      ...prev,
                      seasonal_effects: { 
                        ...prev.seasonal_effects, 
                        confetti_auto_enabled: !prev.seasonal_effects.confetti_auto_enabled 
                      }
                    }))}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      appSettings.seasonal_effects.confetti_auto_enabled 
                        ? 'bg-yellow-500' 
                        : 'bg-gray-300'
                    }`}
                  >
                    <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      appSettings.seasonal_effects.confetti_auto_enabled 
                        ? 'translate-x-8' 
                        : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                {appSettings.seasonal_effects.confetti_auto_enabled && (
                  <div className="ml-8 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
                    <div className="flex items-center gap-2 text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>Zeitraum: 31. Dez - 2. Jan</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Cherry Blossom Effect (Spring) */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <Flower2 className="w-5 h-5 text-pink-600" />
                    <div>
                      <p className="text-gray-900 font-medium">🌸 Kirschblüten (Frühling)</p>
                      <p className="text-sm text-gray-500">Frühlingshafte Blütenblätter</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setAppSettings(prev => ({
                      ...prev,
                      seasonal_effects: { 
                        ...prev.seasonal_effects, 
                        cherry_blossom_auto_enabled: !prev.seasonal_effects.cherry_blossom_auto_enabled 
                      }
                    }))}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      appSettings.seasonal_effects.cherry_blossom_auto_enabled 
                        ? 'bg-pink-500' 
                        : 'bg-gray-300'
                    }`}
                  >
                    <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      appSettings.seasonal_effects.cherry_blossom_auto_enabled 
                        ? 'translate-x-8' 
                        : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                {appSettings.seasonal_effects.cherry_blossom_auto_enabled && (
                  <div className="ml-8 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
                    <div className="flex items-center gap-2 text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>Zeitraum: 20. Mär - 30. Apr</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Fireworks Effect (New Year's Eve) */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">🎆</span>
                    <div>
                      <p className="text-gray-900 font-medium">🎆 Feuerwerk (Silvester)</p>
                      <p className="text-sm text-gray-500">Explosives Neujahrs-Feuerwerk</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setAppSettings(prev => ({
                      ...prev,
                      seasonal_effects: { 
                        ...prev.seasonal_effects, 
                        fireworks_auto_enabled: !prev.seasonal_effects.fireworks_auto_enabled 
                      }
                    }))}
                    className={`relative w-14 h-7 rounded-full transition-colors ${
                      appSettings.seasonal_effects.fireworks_auto_enabled 
                        ? 'bg-orange-500' 
                        : 'bg-gray-300'
                    }`}
                  >
                    <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      appSettings.seasonal_effects.fireworks_auto_enabled 
                        ? 'translate-x-8' 
                        : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                {appSettings.seasonal_effects.fireworks_auto_enabled && (
                  <div className="ml-8 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
                    <div className="flex items-center gap-2 text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>Zeitraum: 31. Dez - 1. Jan (Mitternacht)</span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Info Box */}
              <div className="p-4 bg-gradient-to-r from-blue-500/10 via-yellow-500/10 to-pink-500/10 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600">
                  💡 <strong>Saisonale Effekte</strong> werden automatisch im festgelegten Zeitraum aktiviert. 
                  Die Effekte sind in der App-Vorschau live sichtbar.
                </p>
              </div>
            </div>
          </div>
          
          {/* Save Button */}
          <div className="lg:col-span-2 flex justify-end">
            <button
              onClick={saveAppSettings}
              disabled={settingsSaving}
              className="px-6 py-3 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-400 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {settingsSaving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Alle Einstellungen speichern
            </button>
          </div>
        </div>
      )}

      {/* Features & Toggles Tab (Feature-Flags + Zusatz-Push) */}
      {activeTab === 'features' && (
        <FeaturesToggleTab token={token} />
      )}

      {/* Startup-Banner Tab (Announcement, Seasonal Wishes, Promo) */}
      {activeTab === 'startup-banner' && (
        <StartupBannerTab token={token} />
      )}

      {/* Promote to Mobile Tab — Banner im Web-Portal für App-Download */}
      {activeTab === 'mobile-promo' && (
        <MobilePromoTab token={token} />
      )}

      {/* App Store Studio — Marketing-Screenshots für App Store + Play Store */}
      {activeTab === 'app-store-studio' && (
        <AppStoreStudio />
      )}

      {/* Push-Status Tab (Welche Kunden haben die App installiert?) */}
      {activeTab === 'push-status' && (
        <div className="space-y-6">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900" data-testid="push-moved-hint">
            💡 Push-Nachrichten <strong>versenden &amp; automatisieren</strong> (Broadcast, Zielgruppen, Reminder, Upsell, A/B) findest du jetzt zentral unter <strong>Marketing &amp; Analyse → Push-Nachrichten / Automationen</strong>. Hier bleiben nur App-technische Einstellungen (App-Version, Installations-Übersicht).
          </div>
          {/* In-App-Update-Hinweis (App-Version-Check) */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <AppVersionConfig />
          </div>
          {/* Admin-App Push-Diagnose (registrierte Geräte + Test-Push) */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <AdminPushStatusCard />
          </div>
          {/* Bestehende App-Install-Übersicht */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <PushStatusOverview />
          </div>
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && storeInfo && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Android Card */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-yellow-500/20 rounded-xl">
                <PlayCircle className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Android</h3>
                <p className="text-sm text-gray-500">Google Play Store</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">App Name</span>
                <span className="text-gray-900">{storeInfo.android.app_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Bundle ID</span>
                <span className="text-gray-900 font-mono text-xs">{storeInfo.android.bundle_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Version</span>
                <span className="text-gray-900">{storeInfo.android.current_version}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Status</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  storeInfo.android.status === 'published' 
                    ? 'bg-yellow-500/20 text-yellow-600'
                    : 'bg-yellow-500/20 text-yellow-600'
                }`}>
                  {storeInfo.android.status === 'published' ? 'Veröffentlicht' : 'In Entwicklung'}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Erforderliche Dateien:</p>
              {storeInfo.android.requirements.required_files.map(file => (
                <div key={file.name} className="flex items-center gap-2 text-sm py-1">
                  {file.status === 'present' ? (
                    <CheckCircle className="w-4 h-4 text-yellow-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className={file.status === 'present' ? 'text-gray-600' : 'text-gray-500'}>
                    {file.name}
                  </span>
                </div>
              ))}
            </div>

            <a
              href={storeInfo.android.play_console_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 w-full py-2 bg-gray-200 rounded-lg text-gray-900 text-sm font-medium hover:bg-gray-300 flex items-center justify-center gap-2"
            >
              Google Play Console öffnen
            </a>
          </div>

          {/* iOS Card */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <Apple className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">iOS</h3>
                <p className="text-sm text-gray-500">Apple App Store</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">App Name</span>
                <span className="text-gray-900">{storeInfo.ios.app_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Bundle ID</span>
                <span className="text-gray-900 font-mono text-xs">{storeInfo.ios.bundle_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Version</span>
                <span className="text-gray-900">{storeInfo.ios.current_version}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Status</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  storeInfo.ios.status === 'published' 
                    ? 'bg-yellow-500/20 text-yellow-600'
                    : 'bg-yellow-500/20 text-yellow-600'
                }`}>
                  {storeInfo.ios.status === 'published' ? 'Veröffentlicht' : 'In Entwicklung'}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">Erforderliche Dateien:</p>
              {storeInfo.ios.requirements.required_files.map(file => (
                <div key={file.name} className="flex items-center gap-2 text-sm py-1">
                  {file.status === 'present' ? (
                    <CheckCircle className="w-4 h-4 text-yellow-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500" />
                  )}
                  <span className={file.status === 'present' ? 'text-gray-600' : 'text-gray-500'}>
                    {file.name}
                  </span>
                </div>
              ))}
            </div>

            <a
              href={storeInfo.ios.app_store_connect_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 w-full py-2 bg-gray-200 rounded-lg text-gray-900 text-sm font-medium hover:bg-gray-300 flex items-center justify-center gap-2"
            >
              App Store Connect öffnen
            </a>
          </div>
        </div>
      )}

      {/* Deployment Tab */}
      {activeTab === 'deployment' && buildStatus && (
        <div className="space-y-6">
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
            <p className="text-sm text-yellow-600">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              Für den finalen Build und Upload benötigen Sie Android Studio (Android) oder Xcode auf macOS (iOS). 
              Nutzen Sie "Save to Github" um den Code lokal zu klonen.
            </p>
          </div>

          {/* Build Commands */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <PlayCircle className="w-6 h-6 text-yellow-500" />
                <h3 className="text-lg font-semibold text-gray-900">Android Build</h3>
              </div>
              
              <div className={`p-3 rounded-lg mb-4 ${
                buildStatus.android.ready_for_build 
                  ? 'bg-yellow-500/10 border border-green-500/30' 
                  : 'bg-red-500/10 border border-red-500/30'
              }`}>
                <p className={`text-sm ${
                  buildStatus.android.ready_for_build ? 'text-yellow-600' : 'text-red-500'
                }`}>
                  {buildStatus.android.ready_for_build 
                    ? '✓ Bereit zum Builden' 
                    : '✗ Nicht bereit - Dateien fehlen'}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm text-gray-600 mb-4">
                {buildStatus.android.build_command}
              </div>

              <p className="text-xs text-gray-500">
                Output: <code className="text-gray-500">{buildStatus.android.output_path}</code>
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <Apple className="w-6 h-6 text-blue-500" />
                <h3 className="text-lg font-semibold text-gray-900">iOS Build</h3>
              </div>
              
              <div className={`p-3 rounded-lg mb-4 ${
                buildStatus.ios.ready_for_build 
                  ? 'bg-yellow-500/10 border border-green-500/30' 
                  : 'bg-red-500/10 border border-red-500/30'
              }`}>
                <p className={`text-sm ${
                  buildStatus.ios.ready_for_build ? 'text-yellow-600' : 'text-red-500'
                }`}>
                  {buildStatus.ios.ready_for_build 
                    ? '✓ Bereit zum Builden' 
                    : '✗ Nicht bereit - Dateien fehlen'}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm text-gray-600 mb-4">
                {buildStatus.ios.build_command}
              </div>

              <p className="text-xs text-gray-500">
                {buildStatus.ios.note}
              </p>
            </div>
          </div>

          {/* Documentation Link */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-purple-500" />
              <h3 className="text-lg font-semibold text-gray-900">Deployment Dokumentation</h3>
            </div>
            <p className="text-gray-500 mb-4">
              Eine vollständige Anleitung für die Veröffentlichung in Google Play Store und Apple App Store 
              finden Sie in der Projektdokumentation.
            </p>
            <div className="flex gap-3">
              <a
                href={`${API}/admin/mobile-app/download-project`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                STORE_DEPLOYMENT.md
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Download Tab */}
      {activeTab === 'download' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <Download className="w-6 h-6 text-yellow-500" />
              <h3 className="text-lg font-semibold text-gray-900">Capacitor React Projekt</h3>
            </div>
            
            <p className="text-gray-500 mb-6">
              Das Projekt basiert auf <code className="text-yellow-600">Capacitor 6 + React (CRA)</code>. 
              Nutzen Sie "Save to Github" im Chat, um den Code zu sichern und lokal zu klonen.
            </p>

            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-2">1. Code klonen (nach Github-Push)</p>
                <code className="text-yellow-600 text-sm">git clone [your-repo-url] && cd frontend</code>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-2">2. Dependencies installieren</p>
                <code className="text-yellow-600 text-sm">yarn install</code>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-2">3. Production Build erstellen</p>
                <code className="text-yellow-600 text-sm">REACT_APP_BACKEND_URL=https://api.strongfit.ch yarn build</code>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-2">4. Native Projekte synchronisieren</p>
                <code className="text-yellow-600 text-sm">npx cap sync</code>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500 mb-2">5. App in IDE öffnen</p>
                <div className="flex gap-4 mt-2">
                  <code className="text-yellow-600 text-sm">npx cap open android</code>
                  <span className="text-gray-600">|</span>
                  <code className="text-yellow-600 text-sm">npx cap open ios</code>
                </div>
              </div>
            </div>
          </div>

          {/* Project Structure */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Projektstruktur</h3>
            <pre className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600 overflow-x-auto">
{`frontend/
├── src/
│   ├── pages/
│   │   └── MobileStandaloneApp.jsx    # Mobile App Entry
│   ├── components/admin/mobile-preview/
│   │   └── screens/                   # 38+ Mobile Screens
│   ├── utils/
│   │   ├── capacitor.js               # Native Bridge
│   │   └── pushNotifications.js       # Push Notifications
│   └── i18n/locales/                  # DE, EN, FR, IT, ES
├── android/                           # Android (Capacitor)
├── ios/                               # iOS (Capacitor)
├── capacitor.config.json              # Capacitor Config
├── NATIVE_BUILD.md                    # Build-Anleitung
└── package.json                       # Dependencies`}
            </pre>
          </div>

          {/* Quick Commands */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Schnellbefehle</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { cmd: 'yarn cap:build', desc: 'Web bauen + native sync' },
                { cmd: 'yarn cap:sync', desc: 'Nur native sync' },
                { cmd: 'yarn cap:android', desc: 'Android Studio öffnen' },
                { cmd: 'yarn cap:ios', desc: 'Xcode öffnen' },
                { cmd: 'yarn cap:run:android', desc: 'Auf Android-Gerät starten' },
                { cmd: 'yarn cap:run:ios', desc: 'Auf iOS-Gerät starten' },
              ].map(({ cmd, desc }) => (
                <div key={cmd} className="bg-gray-50 rounded-lg p-3 flex justify-between items-center">
                  <code className="text-yellow-600 text-sm">{cmd}</code>
                  <span className="text-gray-500 text-xs">{desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileAppManagement;

// =====================================================================
// FeaturesToggleTab — App Features & Zusatz-Push (vorher in KI & API)
// =====================================================================

const FEATURE_DEFS = [
  // Core
  { key: 'feature_photo_analysis', label: 'Foto-Analyse', desc: 'KI analysiert Mahlzeit-Fotos', icon: Camera, color: 'text-blue-600', group: 'Kern-Features' },
  { key: 'feature_barcode_scanner', label: 'Barcode-Scanner', desc: 'Produkte per Strichcode erfassen', icon: BarChart3, color: 'text-green-600', group: 'Kern-Features' },
  { key: 'feature_ai_coaching', label: 'KI-Coaching', desc: 'Health-Coach & AI-Mahlzeitenassistent', icon: SparklesIcon, color: 'text-purple-600', group: 'Kern-Features' },
  { key: 'feature_gamification', label: 'Gamification', desc: 'Achievements, Punkte, Challenges', icon: Trophy, color: 'text-yellow-600', group: 'Kern-Features' },
  { key: 'feature_health_kit_sync', label: 'Health Kit Sync', desc: 'Apple Health / Google Fit Anbindung', icon: Heart, color: 'text-rose-400', group: 'Kern-Features' },
  { key: 'feature_smartwatch_support', label: 'Smartwatch Support', desc: 'Apple Watch / Wear OS', icon: Watch, color: 'text-cyan-400', group: 'Kern-Features' },

  // Nutrition & Training
  { key: 'feature_meal_distribution', label: 'Mahlzeiten-Verteilung', desc: 'Kreisdiagramm (Frühstück/Mittag/Abend/Snacks)', icon: BarChart3, color: 'text-orange-600', group: 'Ernährung & Training' },
  { key: 'feature_workout_history_rpe', label: 'Training mit RPE/Notizen', desc: 'Erweiterte Workout-Historie', icon: Zap, color: 'text-red-500', group: 'Ernährung & Training' },
  { key: 'feature_wod_plan', label: 'Workout of the Day', desc: 'Tages-Trainingsplan im Workout-Screen', icon: Trophy, color: 'text-amber-600', group: 'Ernährung & Training' },

  // Account & Finance
  { key: 'feature_transaction_history', label: 'Transaktions-Verlauf', desc: 'Full-View aller Transaktionen', icon: BarChart3, color: 'text-emerald-600', group: 'Konto & Abrechnung' },
  { key: 'feature_points_sharing', label: 'Punkte teilen', desc: 'Familie/Partner Einladen', icon: Users, color: 'text-violet-400', group: 'Konto & Abrechnung' },
  { key: 'feature_insurance_documents', label: 'Versicherungs-Dokumente', desc: 'Abonnement-Bestätigungen / Jahresformulare', icon: Heart, color: 'text-sky-400', group: 'Konto & Abrechnung' },
  { key: 'feature_open_items', label: 'Offene Positionen', desc: 'Unbezahlte Rechnungen + unabgerechnete Termine', icon: Megaphone, color: 'text-orange-600', group: 'Konto & Abrechnung' },
  { key: 'feature_my_packages', label: 'Mein Paket', desc: 'Abo-Detail + Kündigung im Flutter-Profil', icon: Trophy, color: 'text-emerald-600', group: 'Konto & Abrechnung' },

  // Onboarding & UX
  { key: 'feature_profile_completion', label: 'Profil-Vollständigkeit', desc: 'Checkliste + Home-Banner', icon: Users, color: 'text-lime-400', group: 'Onboarding & UX' },
  { key: 'feature_welcome_disclaimer', label: 'Welcome Disclaimer', desc: 'AGB/Disclaimer beim 1. Login', icon: SparklesIcon, color: 'text-teal-400', group: 'Onboarding & UX' },
  { key: 'feature_onboarding_walkthrough', label: 'Onboarding Tour', desc: '4-stufige Einführung beim ersten Start', icon: SparklesIcon, color: 'text-amber-600', group: 'Onboarding & UX' },
  { key: 'feature_startup_banner', label: 'Startup-Banner', desc: 'Promo/Saisonale Begrüssung bei App-Start', icon: Megaphone, color: 'text-pink-600', group: 'Onboarding & UX' },
  { key: 'feature_seasonal_effects', label: 'Saisonale Effekte', desc: 'Schnee/Blumen/Herbstblätter (auto)', icon: SparklesIcon, color: 'text-indigo-400', group: 'Onboarding & UX' },
  { key: 'feature_addon_service_popup', label: 'Addon-Service Upsell', desc: 'Popup nach erfolgreicher Buchung', icon: SparklesIcon, color: 'text-fuchsia-400', group: 'Onboarding & UX' },
  { key: 'feature_health_coach_home_card', label: 'KI-Coach Home-Card', desc: 'Kompakte Vorschau auf dem Home-Screen', icon: SparklesIcon, color: 'text-yellow-600', group: 'Onboarding & UX' },
];

const PUSH_DEFS = [
  { key: 'push_appointment_reminders', label: 'Termin-Erinnerungen', desc: '24h und 1h vor dem Termin' },
  { key: 'push_nutrition_reminders', label: 'Ernährungs-Erinnerungen', desc: 'Tägliche Mahlzeit-Tracking Erinnerung' },
  { key: 'push_training_motivation', label: 'Trainings-Motivation', desc: 'Motivierende Nachrichten an Trainingstagen' },
  { key: 'push_water_reminders', label: 'Wasser-Erinnerungen', desc: 'Alle 2 Stunden (9-21 Uhr)' },
  { key: 'push_progress_updates', label: 'Fortschritts-Updates', desc: 'Wöchentliche Zusammenfassung' },
];

const DEFAULT_SETTINGS = {
  app_name: '',
  app_slogan: 'Dein Personal Trainer',
  primary_color: '#EAB308',
  accent_color: '#10B981',
  push_appointment_reminders: true,
  push_nutrition_reminders: true,
  push_training_motivation: false,
  push_water_reminders: false,
  push_progress_updates: true,
  feature_photo_analysis: true,
  feature_barcode_scanner: true,
  feature_ai_coaching: true,
  feature_gamification: true,
  feature_health_kit_sync: false,
  feature_smartwatch_support: false,
};

const FeaturesToggleTab = ({ token }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/mobile-app/app-features`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Backend hat zwei Schemas — wir mergen defensiv
        setSettings({ ...DEFAULT_SETTINGS, ...(res.data || {}) });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const toggle = (key) => setSettings((s) => ({ ...s, [key]: !s[key] }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/mobile-app/app-features`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Features gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500">Lade Features…</div>;
  }

  return (
    <div className="space-y-6">
      {/* Feature Flags */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm" data-testid="features-toggle-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-yellow-500/20 rounded-xl">
            <Zap className="w-6 h-6 text-yellow-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Feature-Flags</h3>
            <p className="text-sm text-gray-500">Schalte einzelne App-Module zentral an/aus</p>
          </div>
        </div>
        <div className="space-y-6">
          {['Kern-Features', 'Ernährung & Training', 'Konto & Abrechnung', 'Onboarding & UX'].map((groupName) => (
            <div key={groupName}>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{groupName}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {FEATURE_DEFS.filter(f => f.group === groupName).map((f) => {
                  const Icon = f.icon;
                  return (
                    <div
                      key={f.key}
                      className="flex items-center justify-between p-3 bg-gray-50 border border-gray-300 rounded-lg"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Icon className={`w-5 h-5 flex-shrink-0 ${f.color}`} />
                        <div className="min-w-0">
                          <p className="text-gray-900 font-medium truncate">{f.label}</p>
                          <p className="text-xs text-gray-500 truncate">{f.desc}</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings[f.key] !== false}
                        onCheckedChange={() => toggle(f.key)}
                        data-testid={`feature-toggle-${f.key}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Push Toggles */}
      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm" data-testid="push-toggles-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-purple-500/20 rounded-xl">
            <Bell className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Push-Standard-Benachrichtigungen</h3>
            <p className="text-sm text-gray-500">
              Globale Voreinstellung — jeder Kunde kann in seinem Profil einzelne abbestellen
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {PUSH_DEFS.map((p) => (
            <div
              key={p.key}
              className="flex items-center justify-between p-3 bg-gray-50 border border-gray-300 rounded-lg"
            >
              <div>
                <p className="text-gray-900 font-medium">{p.label}</p>
                <p className="text-xs text-gray-500">{p.desc}</p>
              </div>
              <Switch
                checked={!!settings[p.key]}
                onCheckedChange={() => toggle(p.key)}
                data-testid={`push-toggle-${p.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="px-6 py-2 bg-yellow-500 text-black font-medium rounded-lg hover:bg-yellow-400 disabled:opacity-60 flex items-center gap-2"
          data-testid="save-features-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Speichern…' : 'Features speichern'}
        </button>
      </div>
    </div>
  );
};

